import {
  FormControl,
  FormLabel,
  GridItem,
  HStack,
  Input,
  Link,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  Tooltip
} from '@chakra-ui/react';
import { FC } from 'react';
import { ProgramToken } from 'types';
import { UseFormRegister } from 'react-hook-form';
import { DeploymentFormErrors } from '..';
import { useTokenBalance } from 'hooks/useTokenBalanceForm';
import { FormError } from 'components/onboarding/form/FormError';
import { IDeploymentForm } from '../type';
import StepBox from 'components/onboarding/deployment/stepBox';
import SelectTokenInput from './selectTokenInput';
import { RiInformationLine } from 'react-icons/ri';

type Props = {
  tokenList: ProgramToken[];
  setRewardToken: React.Dispatch<
    React.SetStateAction<ProgramToken | undefined>
  >;
  rewardToken: ProgramToken | undefined;
  register: UseFormRegister<IDeploymentForm>;
  errors: DeploymentFormErrors;
};

const SelectRewardToken: FC<Props> = ({
  tokenList,
  setRewardToken,
  rewardToken,
  register,
  errors
}) => {
  const { insufficientBalance, zeroAmount, nullAmount } = useTokenBalance(
    'rewardTokenAmount',
    rewardToken
  );

  return (
    <StepBox heading='3. Select Reward Token'>
      <GridItem colSpan={[1, 2, 3, 4]}>
        <Text color='gray.200'>
          This is the primary reward token that you will be using to reward
          users. This token is used for all the primary accounting. You can use
          any type of ERC20 token including rebasing tokens.{' '}
          <Link
            href='https://docs.crucible.wtf/launching-a-reward-program/reward-program-configuration#funding-events'
            isExternal
            color='cyan.400'
          >
            Read more here.
          </Link>
        </Text>
      </GridItem>

      <GridItem>
        <SelectTokenInput
          tokenList={tokenList}
          setSelectedToken={setRewardToken}
          required
        />
      </GridItem>

      <GridItem>
        <FormControl
          isInvalid={!!errors.rewardTokenAmount}
          isDisabled={!rewardToken}
        >
          <FormLabel>
            <HStack>
              <Text whiteSpace='nowrap' overflow='hidden'>
                Enter amount (optional)
              </Text>
              <Tooltip
                label={`The amount of your selected reward token 
                that you would like to fund your program with at creation time.
                You can always fund your reward program at a later time.`}
                placement='top'
                hasArrow
              >
                <Text>
                  <RiInformationLine fontSize='18px' />
                </Text>
              </Tooltip>
            </HStack>
          </FormLabel>
          <Input
            id='rewardTokenAmount'
            type='number'
            step='any'
            borderColor={'gray.300'}
            placeholder='0'
            defaultValue={0}
            {...register('rewardTokenAmount')}
          />
          {insufficientBalance && <FormError>Insufficient balance</FormError>}
        </FormControl>
      </GridItem>

      <GridItem>
        <FormControl
          isInvalid={!!errors.unlockDays}
          isDisabled={insufficientBalance || nullAmount || zeroAmount}
        >
          <FormLabel>
            <HStack>
              <Text>Enter unlock days</Text>
              <Tooltip
                label={`
                  The number of days over which the reward token amount you (optionally) select
                  will be released to the reward pool. 
                `}
                placement='top'
                hasArrow
              >
                <Text>
                  <RiInformationLine fontSize='18px' />
                </Text>
              </Tooltip>
            </HStack>
          </FormLabel>
          <NumberInput defaultValue={0} min={0}>
            <Input
              as={NumberInputField}
              id='unlockDays'
              type='number'
              borderColor={'gray.300'}
              placeholder='0'
              defaultValue={0}
              {...register('unlockDays')}
            />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          {errors.unlockDays && (
            <FormError>{errors.unlockDays.message}</FormError>
          )}
        </FormControl>
      </GridItem>
    </StepBox>
  );
};

export default SelectRewardToken;
